import Button from 'components/_parts/atoms/button';
import Input from 'components/_parts/atoms/input';
import Label from 'components/_parts/atoms/label';
import TextEditor from 'components/_parts/atoms/textEditor';

import { Formik, useFormik } from 'formik';
import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import { useCreateCompLibrary, useUpdateCompLibrary } from 'components/company/api/useCompetencyAndSkillGroup';
import styled from 'styled-components';
import CollapsibleEdit from './collapsibleEdit';
import customHooks from './customHooks';
import UseFetchData from './fetchData';
import './index.scss';
import InfoComp from './infoComp';
import initialValues from './initialValues';
import { schemaData, schemaTempData } from './validationSchema';

const FullScreenOverlay = styled.div`
  position: fixed;
  height: 100%;
  width: 100%;
  right: 0;
  right: 0;
  top: 0;
`;

const InputMod = styled(Input)`
  max-width: ${(props) => props?.maxwidth ?? '279px'};
  height: 40px;
  width: ${(props) => props?.width ?? 'inherit'};
`;

const ButtonMod = styled(Button)`
  min-width: 92px;
  height: 40px;
  padding: 13px 10px;
`;

function Index(props) {
  const {
    data, t, typeForm, modal, typeCompetency, funcModal,
  } = props;

  const [initialValuesMod, setInitialValuesMod] = useState(initialValues);
  const [listField, setListField] = useState([{ id: Date.now(), value: '' }]);
  const [isOpen, setIsOpen] = useState(0);

  const { handleAddField, handleRemoveField, emptyEvent } = customHooks({
    setListField,
    listField,
    initialValues,
  });

  const { mutate: createCompetency } = useCreateCompLibrary();

  const { mutate: updateCompetency } = useUpdateCompLibrary();

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: schemaData({ typeCompetency, t }),
    initialValues: initialValuesMod,
    onSubmit: (values) => {
      let tempDetail = [];

      values.data.competency_detail.forEach((competency) => {
        tempDetail = tempDetail.concat(
          competency.detail.map((detail, index) => ({
            competency_level: detail.competency_level,
            value: detail.value,
            competency_type: competency.competency_type,
            competency_group: competency.competency_group,
            description_sub_skill: competency.description_sub_skill,
            order: props?.typeForm !== 'edit' ? detail.order : index + 1, // Use tempDetail.length after concatenation
            competency_detail_id: detail.competency_detail_id || null, // Handle optional id
            action: detail.competency_id ? 'update' : 'create',
            performance_criteria: detail.performance_criteria,
            experience: detail.experience,
            learning_development: detail.learning_development,
            evidence: detail.evidence,
          })),
        );
      });

      const tempFinal = {
        company_id: localStorage.getItem('company_id'),
        competency_id: data.competency_id ?? undefined,
        status: 'active',
        type: props?.typeCompetency,
        is_default: false,
        title: values.data.title,
        detail: tempDetail,
      };

      updateCompetency(tempFinal);

      formik.resetForm();
      props?.funcModal(false);
    },
  });

  const handleSubmit = async (values) => {
    let tempDetail = [];

    values.tempData.competency_detail.forEach((competency) => {
      tempDetail = tempDetail.concat(
        competency.detail.map((detail, index) => ({
          competency_level: detail.competency_level,
          value: detail.value,
          competency_type: competency.competency_type,
          competency_group: competency.competency_group,
          description_sub_skill: competency.description_sub_skill,
          order: detail.order, // Use tempDetail.length after concatenation
          competency_detail_id: detail.competency_detail_id || null, // Handle optional id
          competency_id: detail.competency_id || null, // Handle optional id
          action: 'create',
          performance_criteria: detail.performance_criteria,
          experience: detail.experience,
          learning_development: detail.learning_development,
          evidence: detail.evidence,
        })),
      );
    });

    const tempFinal = {
      company_id: localStorage.getItem('company_id'),
      status: 'active',
      type: props?.typeCompetency,
      is_default: false,
      title: values.data.title,
      detail: tempDetail,
    };

    createCompetency(tempFinal);

    setInitialValuesMod(initialValues);
    funcModal(false);
  };

  const fetchData = UseFetchData({
    setInitialValuesMod,
    initialValues,
    typeForm,
    data,
    modal,
    skillGroup: formik.values,
  });

  const {
    listSkillGroup,
    listSubSkillGroup,
    keyListSkillGroup,
    keySubSkillGroup,
  } = useMemo(() => fetchData, [fetchData]);

  const isTechnical = typeCompetency === 'technical_competency';

  return (
    props?.modal && (
      <>
        {createPortal(
          <>
            <FullScreenOverlay
              className="cs_modal-bg"
              onClick={() => {
                funcModal(false);
                formik.resetForm();
              }}
            />
            <div className="competency_lib-container" ref={props?.ref}>
              <InfoComp tab={typeCompetency} />
              {typeForm === 'create' && (
                <Formik
                  enableReinitialize
                  validationSchema={schemaTempData({ typeCompetency, t })}
                  initialValues={initialValuesMod}
                  onSubmit={handleSubmit}
                >
                  {({
                    setValues,
                    values: valuesParent,
                    errors,
                    touched,
                    handleChange,
                    resetForm,
                    setFieldValue,
                    handleSubmit,
                  }) => (
                    <div className="competency_lib-left">
                      <div className="d-flex flex-row align-items-center mb-5">
                        <Label
                          style={{ marginRight: '50px' }}
                          typeLabel="label_modal"
                        >
                          {t('company.new_competency')}
                        </Label>
                        <div style={{ width: '53%' }}>
                          <InputMod
                            name="tempData.title"
                            type="text"
                            width="96%"
                            maxwidth="inherit"
                            placeholder={t('company.ins_competency_name')}
                            value={valuesParent.tempData?.title}
                            onChange={(e) => {
                              handleChange(e);
                              setFieldValue('data.title', e.target.value);
                              formik.setFieldValue(
                                'data.title',
                                e.target.value,
                              );
                            }}
                            isError={errors.tempData?.title}
                            validateMessage={errors.tempData?.title}
                          />
                        </div>
                        <div className="d-flex py-1" style={{ gap: '10px', marginLeft: 'auto' }}>
                          <ButtonMod
                            style={{ width: 'fit-content' }}
                            func={() => {
                              funcModal(false);
                              formik.resetForm();
                            }}
                          >
                            {t('board.cancel')}
                          </ButtonMod>
                          <ButtonMod
                            func={handleSubmit}
                          >
                            {t('company.save_comp')}
                          </ButtonMod>
                        </div>
                      </div>

                      <div className="competency_lib-left-form w-100 d-flex flex-column">
                        <div
                          className="d-flex w-100 flex-row"
                        >
                          <div className="w-100 d-flex" style={{ gap: '10px' }}>
                            <div style={{ width: '50%' }}>
                              <Input
                                id="compLibrary-skillGroup"
                                value={listSkillGroup?.find(
                                  (val) => val.value
                                    === valuesParent?.tempData.competency_detail[0]
                                      .competency_group,
                                )}
                                type="dropdown-windowed"
                                name="tempData.competency_detail[0].competency_group"
                                placeholder={t('company.ins_skill_group_name')}
                                options={
                                  keyListSkillGroup ? [] : listSkillGroup
                                }
                                onChange={(e) => {
                                  setFieldValue(
                                    'tempData.competency_detail[0].competency_group',
                                    e?.value,
                                  );
                                  formik.setFieldValue(
                                    'tempData.competency_detail[0].competency_group',
                                    e?.value,
                                  );
                                }}
                                noOptionsMessage={() => (keyListSkillGroup
                                  ? 'Loading Get Data...'
                                  : t(
                                    'org.structure_view.personel.form.p_option',
                                  ))}
                                isError={
                                  errors?.tempData?.competency_detail[0]
                                    .competency_group
                                }
                                validateMessage={
                                  errors?.tempData?.competency_detail[0]
                                    .competency_group
                                }
                              />
                            </div>
                            <div style={{ width: '50%' }}>
                              <Input
                                id="compLibrary-subSkillGroup"
                                value={listSubSkillGroup?.find(
                                  (val) => val.value
                                    === valuesParent?.tempData.competency_detail[0]
                                      .competency_type,
                                )}
                                type="dropdown-windowed"
                                name="tempData.competency_detail[0].competency_type"
                                placeholder={t('company.ins_sub_name')}
                                options={
                                  keySubSkillGroup ? [] : listSubSkillGroup
                                }
                                onChange={(e) => {
                                  setFieldValue(
                                    'tempData.competency_detail[0].competency_type',
                                    e?.value,
                                    true,
                                  );
                                  setFieldValue(
                                    'tempData.competency_detail[0].show_competency_type',
                                    e?.label,
                                    false,
                                  );
                                  formik.setFieldValue(
                                    'tempData.competency_detail[0].competency_type',
                                    e?.value,
                                    true,
                                  );
                                }}
                                disable={
                                  !valuesParent?.tempData
                                    ?.competency_detail?.[0]?.competency_group
                                }
                                noOptionsMessage={() => (keySubSkillGroup
                                  ? 'Loading Get Data...'
                                  : t(
                                    'org.structure_view.personel.form.p_option',
                                  ))}
                                isError={
                                  errors?.tempData?.competency_detail[0]
                                    .competency_type
                                }
                                validateMessage={
                                  errors?.tempData?.competency_detail[0]
                                    .competency_type
                                }
                              />
                            </div>
                          </div>
                        </div>

                        <div>
                          <Label typeLabel="label_option">
                            {t('company.competency_desc')}
                          </Label>
                          <TextEditor
                            id="text-editor-output"
                            name={`tempData.competency_detail[${0}].description_sub_skill`}
                            placeholder={t('company.ins_desc')}
                            style={{ height: '120px', marginTop: '5px' }}
                            value={
                              valuesParent?.tempData?.competency_detail?.[0]
                                ?.description_sub_skill || ''
                            }
                            onChange={(e) => {
                              const value = emptyEvent(e);
                              setFieldValue(
                                `tempData.competency_detail.[${0}].description_sub_skill`,
                                value,
                              );
                            }}
                          />
                          {errors.tempData?.competency_detail?.[0]
                            ?.description_sub_skill ? (
                              <span className="error_form m-0">
                                {
                                errors.tempData?.competency_detail?.[0]
                                  ?.description_sub_skill
                              }
                              </span>
                            ) : null}
                        </div>

                        <div
                          className="scrollCustom pr-1 pl-1"
                          style={{ overflow: 'auto', height: '100%' }}
                        >
                          {valuesParent?.tempData?.competency_detail[0]?.detail?.map(
                            (item, i) => (
                              <div
                                key={i + 1}
                                className="competency_lib-left-form-container"
                              >
                                <div
                                  className="competency_lib-right-form-input my-2"
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    columnGap: '11px',
                                  }}
                                >
                                  <div style={{ width: '70%' }}>
                                    <InputMod
                                      type="text"
                                      maxwidth="inherit"
                                      placeholder={t('company.lvl_name')}
                                      name={`tempData.competency_detail[0].detail[${i}].competency_level`}
                                      value={
                                        valuesParent?.tempData
                                          ?.competency_detail?.[0]?.detail?.[i]
                                          ?.competency_level
                                      }
                                      onChange={handleChange}
                                      isError={
                                        errors.tempData?.competency_detail?.[0]
                                          ?.detail?.[i]?.competency_level
                                      }
                                      validateMessage={
                                        errors.tempData?.competency_detail?.[0]
                                          ?.detail?.[i]?.competency_level
                                      }
                                    />
                                  </div>

                                  <div className="d-flex gap10px">
                                    {valuesParent?.tempData
                                      ?.competency_detail[0]?.detail?.length
                                      > 1 && (
                                      <Button
                                        icon="minus"
                                        style={{
                                          height: '24px',
                                          width: '26px',
                                          backgroundColor: '#FF4F4F',
                                        }}
                                        func={() => handleRemoveField({
                                          data: valuesParent?.tempData
                                            ?.competency_detail?.[0]?.detail,
                                          i,
                                          setFieldValue,
                                        })}
                                      />
                                    )}
                                    <Button
                                      icon="create"
                                      style={{
                                        height: '24px',
                                        width: '26px',
                                        backgroundColor: '#9AFF5B',
                                        visibility:
                                          i
                                          === valuesParent?.tempData
                                            ?.competency_detail[0]?.detail
                                            ?.length
                                            - 1
                                            ? ''
                                            : 'hidden',
                                      }}
                                      func={() => handleAddField({
                                        data: valuesParent?.tempData
                                          ?.competency_detail?.[0]?.detail,
                                        i,
                                        setFieldValue,
                                        setIsOpen,
                                      })}
                                    />
                                    <Button
                                      icon={`${
                                        isOpen === i ? 'down' : 'chevronRight'
                                      }`}
                                      style={{
                                        height: '24px',
                                        width: '26px',
                                        backgroundColor: 'white',
                                        color: '#4C4C4C',
                                      }}
                                      func={() => (isOpen === i
                                        ? setIsOpen(null)
                                        : setIsOpen(i))}
                                    />
                                  </div>
                                </div>

                                <div
                                  className="competency_lib-right-form-btn d-flex"
                                  style={{
                                    columnGap: '5px',
                                  }}
                                >
                                  {isOpen === i && (
                                    <div className="w-100 h-100 mt-3">
                                      {/* HIDE LEVEL DESC */}
                                      {/* <div className="mt-1 mb-2" style={{ width: '100%' }}>
                                    <Label className="my-1" typeLabel="label_option">
                                      {t('company.level_desc')}
                                    </Label>
                                    <InputMod
                                      type="text"
                                      maxwidth="inherit"
                                      placeholder={t('company.ins_desc_')}
                                      placeholderPosition="start"
                                      name={`tempData.competency_detail[0].detail[${i}].value`}
                                      value={
                                          valuesParent?.tempData
                                            ?.competency_detail?.[0]?.detail?.[
                                              i
                                            ]?.value
                                        }
                                      onChange={handleChange}
                                      isError={
                                          errors.tempData
                                            ?.competency_detail?.[0]?.detail?.[
                                              i
                                            ]?.value
                                        }
                                      validateMessage={
                                          errors.tempData
                                            ?.competency_detail?.[0]?.detail?.[
                                              i
                                            ]?.value
                                        }
                                    />
                                  </div> */}
                                      <div
                                        className="d-flex"
                                        style={{
                                          flexWrap: 'wrap',
                                          gap: '10px',
                                        }}
                                      >
                                        <div style={{ width: '49%' }}>
                                          <Label typeLabel="label_option">
                                            {t(
                                              isTechnical
                                                ? 'company.perform_crit'
                                                : 'company.behaviour_indicator',
                                            )}
                                          </Label>
                                          <TextEditor
                                            id="text-editor-output"
                                            name={`tempData.competency_detail[0].detail[${i}].performance_criteria`}
                                            placeholder={t('company.ins_desc_')}
                                            style={{
                                              height: '130px',
                                              marginTop: '5px',
                                            }}
                                            value={
                                              valuesParent?.tempData
                                                ?.competency_detail?.[0]
                                                ?.detail?.[i]
                                                ?.performance_criteria
                                            }
                                            onChange={(e) => {
                                              const value = emptyEvent(e);
                                              setFieldValue(
                                                `tempData.competency_detail[0].detail[${i}].performance_criteria`,
                                                value,
                                              );
                                            }}
                                          />
                                          {errors?.tempData
                                            ?.competency_detail?.[0]?.detail?.[
                                              i
                                            ]?.performance_criteria ? (
                                              <span className="error_form m-0">
                                                {
                                                errors?.tempData
                                                  ?.competency_detail?.[0]
                                                  ?.detail?.[i]
                                                  ?.performance_criteria
                                              }
                                              </span>
                                            ) : null}
                                        </div>

                                        <div style={{ width: '49%' }}>
                                          <Label typeLabel="label_option">
                                            {t('company.field_skill_exp')}
                                          </Label>
                                          <TextEditor
                                            id="text-editor-output"
                                            name={`tempData.competency_detail${[
                                              0,
                                            ]}.detail[${i}].experience`}
                                            placeholder={t('company.ins_desc_')}
                                            style={{
                                              height: '130px',
                                              marginTop: '5px',
                                            }}
                                            value={
                                              valuesParent?.tempData
                                                ?.competency_detail?.[0]
                                                ?.detail?.[i]?.experience || ''
                                            }
                                            onChange={(e) => {
                                              const value = emptyEvent(e);
                                              setFieldValue(
                                                `tempData.competency_detail[0].detail[${i}].experience`,
                                                value,
                                              );
                                            }}
                                          />
                                          {errors.tempData
                                            ?.competency_detail?.[0]?.detail?.[
                                              i
                                            ]?.experience ? (
                                              <span className="error_form mx-0">
                                                {
                                                errors.tempData
                                                  ?.competency_detail?.[0]
                                                  ?.detail?.[i]?.experience
                                              }
                                              </span>
                                            ) : null}
                                        </div>

                                        <div style={{ width: '49%' }}>
                                          <Label typeLabel="label_option">
                                            {t('company.learning_development')}
                                          </Label>
                                          <TextEditor
                                            id="text-editor-output"
                                            name={`tempData.competency_detail[0].detail[${i}].learning_development`}
                                            placeholder={t('company.ins_desc_')}
                                            style={{
                                              height: '130px',
                                              marginTop: '5px',
                                            }}
                                            value={
                                              valuesParent?.tempData
                                                ?.competency_detail?.[0]
                                                ?.detail?.[i]
                                                ?.learning_development || ''
                                            }
                                            onChange={(e) => {
                                              const value = emptyEvent(e);
                                              setFieldValue(
                                                `tempData.competency_detail[0].detail[${i}].learning_development`,
                                                value,
                                              );
                                            }}
                                          />
                                          {errors.tempData
                                            ?.competency_detail?.[0]?.detail?.[
                                              i
                                            ]?.learning_development ? (
                                              <span className="error_form mx-0">
                                                {
                                                errors.tempData
                                                  ?.competency_detail?.[0]
                                                  ?.detail?.[i]
                                                  ?.learning_development
                                              }
                                              </span>
                                            ) : null}
                                        </div>

                                        <div style={{ width: '49%' }}>
                                          <Label typeLabel="label_option">
                                            {t('company.evidence')}
                                          </Label>
                                          <TextEditor
                                            id="text-editor-output"
                                            name={`tempData.competency_detail[0].detail[${i}].evidence`}
                                            placeholder={t('company.ins_desc_')}
                                            style={{
                                              height: '130px',
                                              marginTop: '5px',
                                            }}
                                            value={
                                              valuesParent?.tempData
                                                ?.competency_detail?.[0]
                                                ?.detail?.[i]?.evidence || ''
                                            }
                                            onChange={(e) => {
                                              const value = emptyEvent(e);
                                              setFieldValue(
                                                `tempData.competency_detail[0].detail[${i}].evidence`,
                                                value,
                                              );
                                            }}
                                          />
                                          {errors.tempData
                                            ?.competency_detail?.[0]?.detail?.[
                                              i
                                            ]?.evidence ? (
                                              <span className="error_form mx-0">
                                                {
                                                errors.tempData
                                                  ?.competency_detail?.[0]
                                                  ?.detail?.[i]?.evidence
                                              }
                                              </span>
                                            ) : null}
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ),
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </Formik>
              )}
              {typeForm === 'edit' && (
                <div
                  className="competency_lib-right"
                  style={{ width: '95%' }}
                >
                  <form
                    action=""
                    onSubmit={formik.handleSubmit}
                    className="w-100 h-100"
                  >
                    <div
                      className="d-flex align-items-center justify-content-between"
                    >
                      <div className="d-flex">
                        <Label className="mr-5" typeLabel="label_modal">
                          {t('company.edit_competency')}
                        </Label>
                        <div style={{ width: '300px' }}>
                          <Input
                            type="text"
                            value={formik.values.data.title || ''}
                            maxwidth="inherit"
                            placeholder={t('company.ins_competency_name')}
                            name="data.title"
                            onChange={(e) => {
                              formik.setFieldValue(
                                'data.title',
                                e.target.value,
                              );
                            }}
                            isError={formik.errors.data?.title}
                            validateMessage={formik.errors.data?.title}
                          />
                        </div>
                      </div>
                      <div className="d-flex py-1" style={{ gap: '10px' }}>
                        <ButtonMod
                          style={{ width: 'fit-content' }}
                          func={() => {
                            props?.funcModal(false);
                            formik.resetForm();
                          }}
                        >
                          {t('board.cancel')}
                        </ButtonMod>
                        <ButtonMod
                          style={{ width: 'fit-content' }}
                          func={formik.handleSubmit}
                        >
                          {t('board.save')}
                        </ButtonMod>
                      </div>
                    </div>

                    <div className="competency_lib-right-form d-flex flex-column scrollCustom">
                      {formik.values?.data?.competency_detail?.length > 0 ? (
                        <CollapsibleEdit
                          data={formik.values?.data}
                          change={formik.handleChange}
                          formik={formik}
                          t={t}
                          typeForm={typeForm}
                          setInitialValuesMod={setInitialValuesMod}
                          tab={typeCompetency}
                          keyListSkillGroup={keyListSkillGroup}
                          keySubSkillGroup={keySubSkillGroup}
                          listSkillGroup={listSkillGroup}
                          listSubSkillGroup={listSubSkillGroup}
                        />
                      ) : (
                        <Label
                          typeLabel="label_option"
                          style={{ textAlign: 'center' }}
                        >
                          No Competency Added
                        </Label>
                      )}
                    </div>
                  </form>
                </div>
              )}
            </div>
          </>,
          document.getElementById('pop_up-parent'),
        )}
      </>
    )
  );
}

export default Index;
